import type { SxProps, Theme } from '@mui/material';
import type { AddressAutocompleteOption } from '../../utils/types/addressAutocompleteOption.interface';

import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Grid, Skeleton, Typography } from '@mui/material';

import { Autocomplete } from '@theguarantors/tg-package-material-ui';

import { useAddresses } from './useAddresses';

export type AddressesAutocompleteProps = {
  value: number;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  label?: string;
  name?: string;
  color?: 'primary' | 'secondary';
  size?: 'small' | 'medium';
  showLoadingSkeleton?: boolean;
  inputSx?: SxProps<Theme>;
  onBlur?: () => void;
  onChange: (addressOption: AddressAutocompleteOption | null) => void;
  'data-testid'?: string;
};

const filterOptions = (options: AddressAutocompleteOption[]) => options;

export const AddressesAutocomplete = forwardRef(
  (
    {
      label,
      error,
      required,
      helperText,
      onBlur,
      name,
      disabled,
      value,
      onChange,
      color = 'primary',
      size,
      showLoadingSkeleton = true,
      inputSx,
      'data-testid': dataTestId = 'property-chooser',
    }: AddressesAutocompleteProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const { addresses, isLoading, isSearchLoading, onSearchChange } = useAddresses();

    const createOptionsLabel = useCallback((option: AddressAutocompleteOption) => {
      const isDuplicatePropertyName =
        option.property_name?.trim().toLowerCase() === option.name.trim().toLowerCase();

      const secondaryText = isDuplicatePropertyName ? '' : option.property_name;

      return (
        <Grid
          container
          alignItems="center"
          data-testid={`addresses-autocomplete-option-${option.id}`}
        >
          <Grid>
            <Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
          </Grid>
          <Grid size="grow">
            <span
              style={{
                fontWeight: 700,
              }}
            >
              {option.name}
            </span>
            {secondaryText && (
              <Typography variant="body2" color="text.secondary">
                {secondaryText}
              </Typography>
            )}
          </Grid>
        </Grid>
      );
    }, []);

    const lastMatchedAddressOption = useRef<AddressAutocompleteOption | undefined>(undefined);

    const selectedValue = useMemo(() => {
      const found = addresses?.find((addr) => addr.monolith_building_id === value);
      if (found) {
        return found;
      }
      if (!value) {
        return undefined;
      }
      return lastMatchedAddressOption.current;
    }, [addresses, value]);

    useEffect(() => {
      const found = addresses?.find((addr) => addr.monolith_building_id === value);
      if (found) {
        lastMatchedAddressOption.current = found;
      } else if (!value) {
        lastMatchedAddressOption.current = undefined;
      }
    }, [addresses, value]);

    const handleInputChange = useCallback(
      (_event: React.SyntheticEvent, inputValue: string, reason: string) => {
        if (reason === 'input') {
          onSearchChange(inputValue);
        } else if (reason === 'clear') {
          onSearchChange('');
        }
      },
      [onSearchChange],
    );

    if (isLoading && showLoadingSkeleton) {
      return (
        <Skeleton
          data-testid="addresses-autocomplete-loading-skeleton"
          variant="rounded"
          height={56}
          sx={{ marginBottom: '20px' }}
        />
      );
    }

    return (
      <Autocomplete<AddressAutocompleteOption, false, false, false>
        clearOnBlur={true}
        size={size}
        id={'property-chooser'}
        data-testid={dataTestId}
        options={isSearchLoading ? [] : addresses || []}
        value={selectedValue}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value: AddressAutocompleteOption | null): void => onChange(value)}
        onInputChange={handleInputChange}
        disabled={disabled}
        loading={isSearchLoading}
        loadingText={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
          </Box>
        }
        getOptionLabel={(option: AddressAutocompleteOption) => {
          const showProperty =
            option.property_name &&
            option.property_name.trim().toLowerCase() !== option.name.trim().toLowerCase();
          return showProperty ? `${option.name} - ${option.property_name}` : option.name;
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {createOptionsLabel(option)}
          </li>
        )}
        textFieldAdditionalProps={{
          label,
          error,
          required,
          helperText,
          sx: inputSx,
          onBlur,
          name,
          value,
          ref,
          color,
        }}
      />
    );
  },
);

export const formatAddressListValue = (option: AddressAutocompleteOption) =>
  [option.name, option.city, option.state, option.zip_code].filter(Boolean).join(' ');
